import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase/client'

/**
 * Always-on Supabase realtime subscriptions for a trip's meals and gear.
 *
 * Wire this into the top-level trips layout (not just the right panel) so that
 * React Query caches are invalidated regardless of which panel is open. The
 * right panel's own realtime hooks (useRealtimeMealNeeds / useRealtimeGearNeeds)
 * then provide the more aggressive `refetchQueries` when they are also mounted.
 */
export function useTripRealtimeSync(tripId: string | undefined) {
  const queryClient = useQueryClient()

  const invalidateMeals = useCallback(() => {
    if (!tripId) return
    queryClient.invalidateQueries({ queryKey: ['meals', tripId] })
    queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
  }, [queryClient, tripId])

  const invalidateGear = useCallback(() => {
    if (!tripId) return
    queryClient.invalidateQueries({ queryKey: ['gearNeeds', tripId] })
    queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
  }, [queryClient, tripId])

  useEffect(() => {
    if (!tripId) return

    const mealsChannel = supabase
      .channel(`layout_trip_meals_${tripId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trip_meals', filter: `trip_id=eq.${tripId}` },
        invalidateMeals
      )
      .subscribe()

    const gearNeedsChannel = supabase
      .channel(`layout_trip_gear_needs_${tripId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trip_gear_needs', filter: `trip_id=eq.${tripId}` },
        invalidateGear
      )
      .subscribe()

    // trip_gear is the table used by the mobile app for gear items
    const mobileGearChannel = supabase
      .channel(`layout_trip_gear_${tripId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'trip_gear', filter: `trip_id=eq.${tripId}` },
        invalidateGear
      )
      .subscribe()

    return () => {
      mealsChannel.unsubscribe()
      gearNeedsChannel.unsubscribe()
      mobileGearChannel.unsubscribe()
    }
  }, [tripId, invalidateMeals, invalidateGear])
}
